// ABOUTME: SPEC-26 Phase 4 boot-sequence step 2 — the Enter Boutique consent/gesture gate (Figma 48:5551).
// ABOUTME: Not authentication — the user gesture a voice-first experience needs to unlock audio autoplay.

import React from 'react';
import { RAIL_UNIT, WORDMARK, u } from '../ReelChrome/railGeometry';
import {
  MOBILE_UNIT, mu, muMin, safeTop, MIN_TEXT_PX, MOBILE_PANEL, WORDMARK as MOBILE_WORDMARK,
} from '../ReelChrome/mobileGeometry';
import { useBreakpoint } from '../Projectionist/useViewportSize';
import { useGateBackgroundVideo, DEFAULT_BACKGROUND_VIDEO_SRC } from './useGateBackgroundVideo';

export { DEFAULT_BACKGROUND_VIDEO_SRC };

// The only wordmark vendored today, because Ophion is the only reel tenant. Kept as an overridable
// default rather than an inline literal so a second reel brand gets its own mark instead of
// inheriting this one (AC-10) — the same reason the headline is parameterised.
export const DEFAULT_WORDMARK_SRC = '/reel-chrome/ophion-wordmark@2x.png';

// The Figma export of this background is a 10 MB PNG — for a full-bleed 1920×1080 photograph on the
// first interactive screen, which is the worst place in the app to put ten megabytes. Same pixels as
// WebP (78 KB) with a JPEG fallback (207 KB) for anything too old to take WebP.
export const DEFAULT_BACKGROUND_SRC = '/reel-chrome/enter-boutique-bg.jpg';
export const DEFAULT_BACKGROUND_WEBP_SRC = '/reel-chrome/enter-boutique-bg.webp';

// The still above is not replaced by the looping film, it is the film's floor: it paints first
// (App.js warms the WebP during the boot beat), holds while the film buffers, and remains the
// screen for anyone the film never reaches — reduced-motion, Save-Data, a refused autoplay, an
// unsupported codec. Pass `backgroundVideoSrc={null}` to opt a tenant out entirely.

// Panel geometry — Figma node 48:5554 ("Frame 162"), authored at 3840x2160. Scaled with the same
// `u()` convention the rest of the reel chrome uses, instead of the fixed Tailwind breakpoint sizes
// this screen originally shipped with — those capped the panel at max-w-lg (512px) and the headline
// at text-2xl (24px) regardless of viewport, half again narrower than the design's 1143px-wide panel
// needs for "Welcome to the Ophion shopping experience." to sit on one line, the way the plate shows.
const PANEL_GAP = 45.333;
const PANEL_PADDING = 37.333;
const HEADLINE_FONT = 48;       // reel.text.welcomeHeadline — vendored in tailwind.config.js but
                                 // never wired up; scaled here via `u()` like every other reel size.
const BUTTON_FONT = 26.667;     // reel.text.button
const BUTTON_PAD_X = 32;
const BUTTON_PAD_Y = 24;
// Mobile composition (2026-09-03). This gate rendered the DESKTOP unit on every device, and that
// unit is a fixed 0.43px on anything under 2064px wide — so a phone got an 11.5px button label in
// a 38px button under a 24px-wide wordmark. On a phone it now takes the mobile unit, the mobile
// wordmark (the same 258.667 authored px the reel screen's MobileChrome sets, so the mark does not
// jump between the two screens) and the mobile composition's own button token with its 11px floor.
// The desktop branch is untouched: a laptop renders exactly as before.
const HEADLINE_MIN_PX = 14;

/**
 * @param {{
 *   onEnter: () => void,
 *   brandName: string,
 *   wordmarkSrc?: string,
 *   backgroundSrc?: string,
 *   backgroundWebpSrc?: string|null,
 *   backgroundVideoSrc?: string|null,
 *   primer?: React.ReactNode,   // SPEC-76: the read-first panel; absent = the markup this screen always had
 *   afterButton?: React.ReactNode, // SPEC-82: a block under the button INSIDE the panel; absent = identical markup
 * }} props
 */
function EnterBoutiqueGate({
  onEnter,
  brandName,
  wordmarkSrc = DEFAULT_WORDMARK_SRC,
  backgroundSrc = DEFAULT_BACKGROUND_SRC,
  backgroundWebpSrc = DEFAULT_BACKGROUND_WEBP_SRC,
  backgroundVideoSrc = DEFAULT_BACKGROUND_VIDEO_SRC,
  primer = null,
  afterButton = null,
}) {
  // Joined rather than interpolated so a missing brand_name in the /api/config handshake degrades
  // to "Welcome to the shopping experience." instead of a visible double space.
  const headline = ['Welcome to the', brandName, 'shopping experience.'].filter(Boolean).join(' ');

  // Unmounting this gate (App.js flips `isExperienceStarted` on Enter Boutique) tears the film down
  // via the hook's cleanup — paused, source dropped, in-flight bytes cancelled. Nothing else needs
  // to know the film exists.
  const { videoRef, videoEnabled, videoVisible } = useGateBackgroundVideo(backgroundVideoSrc);
  const isMobile = useBreakpoint() === 'mobile';

  return (
    <div className="fixed inset-0 w-full h-[100dvh] flex items-center justify-center overflow-hidden">
      <picture>
        {backgroundWebpSrc && <source srcSet={backgroundWebpSrc} type="image/webp" />}
        <img
          src={backgroundSrc}
          alt=""
          // Eager + high priority: it is the whole screen, and the loader beat that precedes this
          // has already warmed it (see App.js's boot preload), so there is nothing to defer.
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </picture>
      {videoEnabled && (
        <video
          ref={videoRef}
          // No `src` prop — useGateBackgroundVideo owns the source imperatively, because its
          // teardown removes the attribute and React would not restore it on a remount. See the
          // comment on that assignment; StrictMode makes it bite on every dev session.
          data-testid="gate-background-video"
          // muted+playsInline is what makes autoplay permissible at all; the hook re-pins `muted`
          // imperatively because React has historically dropped it on the first commit.
          autoPlay
          muted
          loop
          playsInline
          // No `poster`: the <picture> underneath already is the poster, and in WebP — a poster
          // attribute would fetch the heavier JPEG a second time for pixels nobody sees.
          preload="auto"
          // Decorative. It carries no information the headline does not, has no audio track worth
          // exposing, and must never take focus ahead of the one button on the screen.
          aria-hidden="true"
          tabIndex={-1}
          disablePictureInPicture
          // Faded in on `playing` rather than shown immediately, so a film that is still buffering
          // (or that never starts) leaves the still photograph on screen instead of a black hole.
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out pointer-events-none"
          style={{ opacity: videoVisible ? 1 : 0 }}
        />
      )}
      <img
        src={wordmarkSrc}
        alt={brandName}
        // Same WORDMARK.widthPct the reel screen's rail uses (railGeometry.js), so the mark renders
        // at an identical width on both screens instead of this gate's own fixed Tailwind height
        // drifting out of step with it (client feedback, 2026-08-28).
        className={`absolute left-1/2 -translate-x-1/2 object-contain${isMobile ? '' : ' top-6 md:top-10'}`}
        style={isMobile
          ? { '--u': MOBILE_UNIT, top: safeTop(MOBILE_WORDMARK.top), width: mu(MOBILE_WORDMARK.w) }
          : { width: `${WORDMARK.widthPct}%` }}
      />
      <div
        className="relative z-10 flex flex-col items-center rounded-reel-gate bg-reel-chrome-panel backdrop-blur-reel-chrome w-max max-w-[92vw] mx-4 text-center"
        style={{ '--u': isMobile ? MOBILE_UNIT : RAIL_UNIT, gap: u(PANEL_GAP), padding: u(PANEL_PADDING) }}
      >
        <p
          className="text-reel-chrome-textPrimary font-reel-body"
          style={{ fontSize: isMobile ? muMin(HEADLINE_FONT, HEADLINE_MIN_PX) : u(HEADLINE_FONT) }}
        >
          {headline}
        </p>
        <button
          onClick={onEnter}
          className="reel-touch-target relative bg-reel-cta-primaryBg text-black font-reel-body rounded-reel-panel tracking-wide hover:bg-white transition-colors whitespace-nowrap"
          style={{
            fontSize: isMobile ? muMin(MOBILE_PANEL.text.button, MIN_TEXT_PX) : u(BUTTON_FONT),
            paddingLeft: u(BUTTON_PAD_X),
            paddingRight: u(BUTTON_PAD_X),
            paddingTop: u(BUTTON_PAD_Y),
            paddingBottom: u(BUTTON_PAD_Y),
          }}
        >
          Enter Boutique
        </button>
        {afterButton}
      </div>
      {/* SPEC-76: after the button in DOM order (tab reaches Enter first) and absolutely placed, so it can
          never move, delay or cover the one control on this screen. Null renders nothing. */}
      {primer}
    </div>
  );
}

export default EnterBoutiqueGate;
